'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useWishlist } from '@/context/wishlist-context';
import { useAuth } from '@/context/auth-context';
import { formatPrice } from '@/lib/shopify';

export default function WishlistPageClient() {
  const { items, removeItem, isLoading: wishlistLoading, itemCount } = useWishlist();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const isLoading = wishlistLoading || authLoading;

  // Loading state
  if (isLoading) {
    return (
      <div style={{ minHeight: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <svg style={{ width: '24px', height: '24px', animation: 'spin 1s linear infinite' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>Loading...</span>
        </div>
        <style jsx>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Empty state - Guest user
  if (itemCount === 0 && !isAuthenticated) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        backgroundColor: '#ffffff'
      }}>
        <div style={{ maxWidth: '600px', textAlign: 'center' }}>
          {/* Heart Icon */}
          <div style={{ marginBottom: '24px' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="#000000"
              style={{ width: '48px', height: '48px', margin: '0 auto' }}
            >
              <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '28px',
            fontWeight: 'bold',
            marginBottom: '20px',
            textTransform: 'uppercase',
            letterSpacing: '2px'
          }}>
            Save to Wishlist
          </h1>

          {/* Description */}
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            lineHeight: '1.6',
            marginBottom: '32px',
            maxWidth: '480px',
            margin: '0 auto 32px'
          }}>
            Save your favourite products in one place to come back to later. Create an account or sign in to start building your wishlist.
          </p>

          {/* Buttons */}
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link
              href="/account/register"
              style={{
                display: 'inline-block',
                padding: '16px 40px',
                backgroundColor: 'var(--brand-blue)',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textDecoration: 'none',
                borderRadius: '50px',
                transition: 'background-color 0.2s ease'
              }}
            >
              Create Account
            </Link>
            <Link
              href="/account/login"
              style={{
                display: 'inline-block',
                padding: '16px 40px',
                backgroundColor: '#6b7280',
                color: '#ffffff',
                fontSize: '14px',
                fontWeight: '600',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                textDecoration: 'none',
                borderRadius: '50px',
                transition: 'background-color 0.2s ease'
              }}
            >
              Log In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Empty state - Logged in user
  if (itemCount === 0 && isAuthenticated) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '48px 24px',
        backgroundColor: '#ffffff'
      }}>
        <div style={{ maxWidth: '500px', textAlign: 'center' }}>
          {/* Heart Icon */}
          <div style={{ marginBottom: '24px' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1}
              stroke="#d1d5db"
              style={{ width: '80px', height: '80px', margin: '0 auto' }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
          </div>

          {/* Title */}
          <h1 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '12px'
          }}>
            Your Wishlist is Empty
          </h1>

          {/* Description */}
          <p style={{
            fontSize: '16px',
            color: '#6b7280',
            lineHeight: '1.6',
            marginBottom: '32px'
          }}>
            Browse our products and click the heart icon to add items to your wishlist.
          </p>

          {/* CTA Button */}
          <Link
            href="/collections"
            style={{
              display: 'inline-block',
              padding: '16px 48px',
              backgroundColor: 'var(--brand-blue)',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: '600',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              textDecoration: 'none',
              transition: 'background-color 0.2s ease'
            }}
          >
            Start Shopping
          </Link>
        </div>
      </div>
    );
  }

  // With items
  return (
    <div style={{ backgroundColor: '#ffffff', minHeight: 'calc(100vh - 200px)' }}>
      {/* Header */}
      <div style={{
        borderBottom: '1px solid #e5e7eb',
        padding: '32px 24px'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="#000000"
              style={{ width: '28px', height: '28px' }}
            >
              <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
            </svg>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
              Your Wishlist
            </h1>
          </div>
          <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
            {itemCount} {itemCount === 1 ? 'item' : 'items'}
          </p>
        </div>
      </div>

      {/* Products Grid */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '24px'
        }}>
          {items.map((item) => (
            <div
              key={item.id}
              style={{
                border: '1px solid #e5e7eb',
                backgroundColor: '#ffffff',
                overflow: 'hidden'
              }}
            >
              {/* Product Image */}
              <Link href={`/products/${item.handle}`} style={{ display: 'block', position: 'relative' }}>
                <div style={{
                  position: 'relative',
                  aspectRatio: '3/4',
                  backgroundColor: '#f5f5f5'
                }}>
                  {item.image ? (
                    <Image
                      src={item.image.url}
                      alt={item.image.altText || item.title}
                      fill
                      style={{ objectFit: 'cover' }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="#d1d5db" style={{ width: '48px', height: '48px' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
                      </svg>
                    </div>
                  )}

                  {/* Remove Button */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      removeItem(item.id);
                    }}
                    style={{
                      position: 'absolute',
                      top: '12px',
                      right: '12px',
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#ffffff',
                      border: 'none',
                      borderRadius: '50%',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      transition: 'transform 0.2s ease'
                    }}
                    aria-label="Remove from wishlist"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="#e11d48"
                      style={{ width: '18px', height: '18px' }}
                    >
                      <path d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
                    </svg>
                  </button>

                  {/* Out of Stock Badge */}
                  {!item.availableForSale && (
                    <div style={{
                      position: 'absolute',
                      top: '12px',
                      left: '12px',
                      backgroundColor: 'var(--color-black)',
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: '600',
                      padding: '4px 8px',
                      textTransform: 'uppercase'
                    }}>
                      Out of Stock
                    </div>
                  )}
                </div>
              </Link>

              {/* Product Info */}
              <div style={{ padding: '16px' }}>
                <p style={{ fontSize: '12px', fontWeight: 700, color: '#1a1a1a', marginBottom: '4px', letterSpacing: '0.05em' }}>
                  OKTSHIRT
                </p>
                <Link
                  href={`/products/${item.handle}`}
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#000000',
                    textDecoration: 'none',
                    marginBottom: '8px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {item.title}
                </Link>

                {/* Price */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>
                    {formatPrice(item.price)}
                  </span>
                  {item.compareAtPrice && parseFloat(item.compareAtPrice.amount) > parseFloat(item.price.amount) && (
                    <span style={{ fontSize: '14px', color: '#9ca3af', textDecoration: 'line-through' }}>
                      {formatPrice(item.compareAtPrice)}
                    </span>
                  )}
                </div>

                {/* Add to Cart Button */}
                <Link
                  href={`/products/${item.handle}`}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '12px',
                    backgroundColor: item.availableForSale ? 'var(--brand-blue)' : '#d1d5db',
                    color: '#ffffff',
                    fontSize: '13px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    textAlign: 'center',
                    textDecoration: 'none',
                    cursor: item.availableForSale ? 'pointer' : 'not-allowed',
                    boxSizing: 'border-box'
                  }}
                >
                  {item.availableForSale ? 'View Product' : 'Out of Stock'}
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Continue Shopping Link */}
        <div style={{ textAlign: 'center', marginTop: '48px' }}>
          <Link
            href="/collections"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#000000',
              textDecoration: 'none',
              fontWeight: '500'
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" style={{ width: '20px', height: '20px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
}
